/** Pages requested at once once page 1 has said how many there are. */
const PARALLEL_PAGES = 4;
const PAGE_SIZE = 100;

function pageRows(response) {
  return response.data || response.results || [];
}

/**
 * Read a complete JSON:API catalog without changing single-page consumers.
 * Calls the supplied page reader with the original filters and request context;
 * next links indicate progress but are never followed as arbitrary URLs.
 *
 * When page 1 reports its page count, the remaining pages are read
 * `PARALLEL_PAGES` at a time instead of one after another; a next-link-only
 * API is still read page by page.
 */
export async function fetchAllPages(fetchPage, queryOptions = {}, requestOptions = {}) {
  const data = new Map();
  const included = new Map();
  let count;

  const readPage = (page) => fetchPage({ pageSize: PAGE_SIZE, ...queryOptions, page }, requestOptions);
  // Merge pages in page order. Repeated or empty pages must not hang or
  // masquerade as a full catalog.
  const merge = (response, isLastPage) => {
    const previousSize = data.size;
    pageRows(response).forEach((resource) => data.set(`${resource.type || 'resource'}:${resource.id}`, resource));
    (response.included || []).forEach((resource) => included.set(`${resource.type}:${resource.id}`, resource));
    count = response.meta?.pagination?.count ?? response.count ?? data.size;
    if (!isLastPage && data.size === previousSize) {
      throw new Error('The catalog did not advance to the next page.');
    }
  };

  const first = await readPage(1);
  const pages = first.meta?.pagination?.pages;
  if (pages != null) {
    const lastPage = Number(pages);
    merge(first, lastPage <= 1);
    for (let start = 2; start <= lastPage; start += PARALLEL_PAGES) {
      const batch = Array.from({ length: Math.min(PARALLEL_PAGES, lastPage - start + 1) }, (_, index) => start + index);
      const responses = await Promise.all(batch.map(readPage));
      responses.forEach((response, index) => merge(response, batch[index] === lastPage));
    }
  } else {
    let response = first;
    let page = 1;
    for (;;) {
      const hasMore = Boolean(response.links?.next || response.next);
      merge(response, !hasMore);
      if (!hasMore) break;
      page += 1;
      response = await readPage(page);
    }
  }

  return { data: [...data.values()], included: [...included.values()], count };
}
